/**
 * HubDemoLauncher - shared landing surface for single-port demo hubs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { Route, Search, Server } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { DEMO_CATALOG, entrySlug } from "../../catalog/catalog-data.js";
import { ProgressBar } from "../ProgressBar.js";
import { StaggeredList } from "../animations/StaggeredList.js";
import { KpiCardGrid } from "../charts/KpiCardGrid.js";
import { HubDemoLauncherCard } from "./HubDemoLauncherCard.js";
import {
  coverageItems,
  isHubReachableView,
  launcherQueryTerms,
  matchesLauncherTerms,
  toHubLauncherDemoView,
  type HubLauncherDemoView,
  type HubLauncherFilter,
} from "./HubDemoLauncherModel.js";

export interface HubDemoLauncherProps {
  readonly wiredSlugs: ReadonlySet<string>;
  readonly hubSlug?: string;
  readonly title?: string;
  readonly eyebrow?: string;
}

const HUB_ROUTE_ICON = <Route size={15} />;
const FALLBACK_ROUTE_ICON = <Server size={15} />;
const ALL_ROUTE_ICON = <Search size={14} />;

interface LauncherSectionProps {
  readonly title: string;
  readonly count: number;
  readonly icon: React.ReactNode;
  readonly views: readonly HubLauncherDemoView[];
  readonly query: string;
}

const FILTERS: readonly {
  readonly id: HubLauncherFilter;
  readonly label: string;
  readonly icon: React.ReactNode;
}[] = [
  { id: "all", label: "All", icon: ALL_ROUTE_ICON },
  { id: "hub", label: "Hub routes", icon: HUB_ROUTE_ICON },
  { id: "fallback", label: "Per-port", icon: FALLBACK_ROUTE_ICON },
];

const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  border: 0,
  clip: "rect(0, 0, 0, 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
};

const CSK_LAUNCHER_BACKGROUND =
  "repeating-linear-gradient(90deg, color-mix(in srgb, var(--csk-accent) 14%, transparent) 0 1px, transparent 1px 96px), " +
  "repeating-linear-gradient(0deg, color-mix(in srgb, var(--csk-contained) 10%, transparent) 0 1px, transparent 1px 96px), " +
  "linear-gradient(112deg, color-mix(in srgb, var(--csk-immune) 20%, var(--csk-void)) 0 20%, transparent 20% 100%), " +
  "linear-gradient(154deg, transparent 0 42%, color-mix(in srgb, var(--csk-contained) 20%, var(--csk-void)) 42% 70%, transparent 70% 100%), " +
  "linear-gradient(24deg, transparent 0 74%, color-mix(in srgb, var(--csk-inert) 16%, var(--csk-void)) 74% 100%), " +
  "var(--csk-void)";

const FilterButton = React.memo(function FilterButton({
  filter,
  active,
  onSelect,
}: {
  readonly filter: (typeof FILTERS)[number];
  readonly active: boolean;
  readonly onSelect: (filter: HubLauncherFilter) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => {
        onSelect(filter.id);
      }}
      className={
        active
          ? "inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-md border border-[var(--csk-accent)] bg-[var(--csk-accent)] px-3 py-1.5 text-xs font-extrabold text-[var(--csk-bright)] shadow-sm"
          : "inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-md border border-transparent bg-transparent px-3 py-1.5 text-xs font-extrabold text-[var(--csk-text)] transition hover:bg-[var(--csk-elevated)] hover:text-[var(--csk-bright)]"
      }
    >
      <span aria-hidden="true" className="inline-flex">
        {filter.icon}
      </span>
      {filter.label}
    </button>
  );
});

const LauncherSection = React.memo(function LauncherSection({
  title,
  count,
  icon,
  views,
  query,
}: LauncherSectionProps): React.JSX.Element | null {
  if (views.length === 0) return null;
  return (
    <section className="grid gap-3">
      <div className="flex items-center gap-2">
        <div
          aria-hidden="true"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--csk-border)] bg-[var(--csk-accent-bg)] text-[var(--csk-accent)]"
        >
          {icon}
        </div>
        <h2 className="m-0 text-[15px] font-extrabold text-[var(--csk-bright)]">{title}</h2>
        <span className="text-xs font-semibold text-[var(--csk-muted)]">{count}</span>
      </div>
      <StaggeredList
        staggerMs={20}
        durationMs={240}
        className="grid grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] items-stretch gap-5"
        triggerKey={`${title}-${query}-${views.length}`}
      >
        {views.map((view) => (
          <HubDemoLauncherCard key={view.slug} view={view} query={query} />
        ))}
      </StaggeredList>
    </section>
  );
});

function partitionViews(
  views: readonly HubLauncherDemoView[],
  query: string,
  filter: HubLauncherFilter,
): {
  readonly visibleCount: number;
  readonly hubViews: readonly HubLauncherDemoView[];
  readonly fallbackViews: readonly HubLauncherDemoView[];
} {
  const terms = launcherQueryTerms(query);
  const hubViews: HubLauncherDemoView[] = [];
  const fallbackViews: HubLauncherDemoView[] = [];
  for (const view of views) {
    if (!matchesLauncherTerms(view, terms)) continue;
    const isHubReachable = isHubReachableView(view);
    if (filter === "hub" && !isHubReachable) continue;
    if (filter === "fallback" && isHubReachable) continue;
    if (isHubReachable) hubViews.push(view);
    else fallbackViews.push(view);
  }
  return { visibleCount: hubViews.length + fallbackViews.length, hubViews, fallbackViews };
}

export function HubDemoLauncher({
  wiredSlugs,
  hubSlug = "hub",
  title = "Every CSK demo, one port",
  eyebrow = "Categorical Safety Kernel - demo hub",
}: HubDemoLauncherProps): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<HubLauncherFilter>("all");
  const views = useMemo(
    () =>
      DEMO_CATALOG.filter((entry) => entrySlug(entry) !== hubSlug).map((entry) =>
        toHubLauncherDemoView(entry, wiredSlugs),
      ),
    [hubSlug, wiredSlugs],
  );
  const visibleViews = useMemo(() => partitionViews(views, query, filter), [filter, query, views]);
  const wiredCount = useMemo(() => {
    let count = 0;
    for (const view of views) if (isHubReachableView(view)) count += 1;
    return count;
  }, [views]);
  const totalCount = views.length;
  const coverage = totalCount === 0 ? 0 : Math.round((wiredCount / totalCount) * 100);
  const coverageCards = useMemo(
    () => coverageItems(wiredCount, totalCount),
    [totalCount, wiredCount],
  );
  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  return (
    <section
      data-theme="light"
      className="relative min-h-screen overflow-hidden px-5 pb-20 pt-10 text-[var(--csk-text)]"
      style={{ background: "var(--csk-void)" }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{ background: CSK_LAUNCHER_BACKGROUND }}
      />
      <div className="relative z-10 mx-auto max-w-[78rem]">
        <header className="mb-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start">
          <div className="grid max-w-[58rem] gap-3">
            <div className="text-xs font-black uppercase text-[var(--csk-accent)]">{eyebrow}</div>
            <h1 className="m-0 text-[2.15rem] font-black leading-[1.04] text-[var(--csk-bright)] sm:text-[2.7rem] lg:text-[3.15rem]">
              {title}
            </h1>
            <p className="m-0 max-w-[52rem] text-[1.02rem] leading-7 text-[var(--csk-text)]">
              {wiredCount} of {totalCount} catalog entries are wired into the single-port hub, with
              per-port workspaces kept visible until migration is complete.
            </p>
          </div>
          <div className="grid gap-3">
            <KpiCardGrid items={coverageCards} minWidth={210} ariaLabel="Hub migration coverage" />
            <ProgressBar
              value={coverage}
              tone={coverage >= 90 ? "success" : "info"}
              ariaLabel="Hub route coverage"
            />
          </div>
        </header>

        <div className="mb-6 flex flex-col gap-3 border-y border-[var(--csk-border)] bg-[color-mix(in_srgb,var(--csk-surface)_76%,transparent)] py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <label className="flex min-h-11 flex-1 items-center gap-2 rounded-md border border-[var(--csk-border)] bg-[var(--csk-surface)] px-3">
            <Search aria-hidden="true" size={16} className="text-[var(--csk-accent)]" />
            <span style={VISUALLY_HIDDEN_STYLE}>Search demo catalog</span>
            <input
              value={query}
              onChange={handleQueryChange}
              placeholder="Search demo catalog"
              className="min-h-10 w-full border-0 bg-transparent text-sm text-[var(--csk-text)] outline-none placeholder:text-[var(--csk-muted)]"
            />
          </label>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <div
              role="group"
              aria-label="Route topology filter"
              className="inline-flex rounded-lg border border-[var(--csk-border)] bg-[color-mix(in_srgb,var(--csk-surface)_82%,transparent)] p-1"
            >
              {FILTERS.map((item) => (
                <FilterButton
                  key={item.id}
                  filter={item}
                  active={filter === item.id}
                  onSelect={setFilter}
                />
              ))}
            </div>
            <span className="ml-1 text-xs font-semibold text-[var(--csk-muted)]">
              {visibleViews.visibleCount} shown
            </span>
          </div>
        </div>

        {visibleViews.visibleCount === 0 ? (
          <div
            role="status"
            className="rounded-lg border border-[var(--csk-border)] bg-[var(--csk-surface)] p-4 text-sm text-[var(--csk-text)]"
          >
            No catalog entries match the current filter.
          </div>
        ) : (
          <div className="grid gap-6">
            <LauncherSection
              title="Hub routes"
              count={visibleViews.hubViews.length}
              icon={HUB_ROUTE_ICON}
              views={visibleViews.hubViews}
              query={query}
            />
            <LauncherSection
              title="Per-port workspaces"
              count={visibleViews.fallbackViews.length}
              icon={FALLBACK_ROUTE_ICON}
              views={visibleViews.fallbackViews}
              query={query}
            />
          </div>
        )}

        <footer className="mt-5 text-xs font-semibold text-[var(--csk-muted)]">
          Route-table coverage is computed from the hub registry at render time.
          <span className="text-[var(--csk-violation)]"> Per-port fallbacks remain explicit.</span>
        </footer>
      </div>
    </section>
  );
}
